/* NCL · Level Off Halloween relaunch: page behavior (page.level-off-halloween).
   Ported from the "Level Off Halloween Relaunch" artifact. Everything is progressive enhancement:
   the page reads, links and scrolls without it, and the buy form still posts to /cart/add.
   Each section is set up on its own, so the theme editor can add, move or reload sections. */
(() => {
  "use strict";
  if (window.__nclHalloween) return;
  window.__nclHalloween = true;

  const root = document.documentElement;
  root.classList.add("nhw-js");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  /* ---------- analytics (window.dataLayer) ---------- */
  window.dataLayer = window.dataLayer || [];
  const PROMO = { promotion_id: "halloween-bogo-2026", promotion_name: "Level Off Halloween BOGO" };
  function item() {
    const el = document.querySelector("[data-nhw-product]");
    const price = el ? Number(el.dataset.nhwPrice) / 100 : undefined;
    return { item_id: el ? el.dataset.nhwProduct : "level-off", item_name: "Level Off", item_brand: "NCL", price, currency: "USD" };
  }
  function track(event, params) {
    window.dataLayer.push({ event, ...params });
  }

  /* ---------- hero stage: old ↔ new reveal with a golden dust burst ---------- */
  function initStage(stage) {
    if (stage.dataset.nhwReady) return;
    stage.dataset.nhwReady = "1";
    const toggle = stage.querySelector("[data-nhw-reveal-toggle]");
    const label = stage.querySelector(".stage__label");
    const canvas = stage.querySelector(".stage__dust");
    const ctx = canvas && canvas.getContext ? canvas.getContext("2d") : null;
    const text = {
      toOld: stage.dataset.labelToOld, toNew: stage.dataset.labelToNew,
      nowOld: stage.dataset.captionOld, nowNew: stage.dataset.captionNew
    };
    let particles = [];
    let ambient = [];
    let rafId = 0;
    let stageVisible = true;

    function sizeCanvas() {
      const r = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(r.width * dpr);
      canvas.height = Math.round(r.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    function seedAmbient() {
      const r = canvas.getBoundingClientRect();
      ambient = Array.from({ length: 26 }, () => ({
        x: Math.random() * r.width, y: Math.random() * r.height,
        vx: (Math.random() - 0.5) * 0.12, vy: -0.08 - Math.random() * 0.18,
        r: 0.6 + Math.random() * 1.6, a: 0.15 + Math.random() * 0.45
      }));
    }
    function burst() {
      const r = canvas.getBoundingClientRect();
      const cx = r.width / 2, cy = r.height * 0.52;
      for (let i = 0; i < 170; i++) {
        const ang = Math.random() * Math.PI * 2;
        const sp = 0.6 + Math.random() * 3.4;
        particles.push({
          x: cx + (Math.random() - 0.5) * r.width * 0.45, y: cy + (Math.random() - 0.5) * r.height * 0.6,
          vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp * 0.8 - 0.6,
          r: 1 + Math.random() * 3.2, life: 1, decay: 0.008 + Math.random() * 0.012
        });
      }
      loop();
    }
    function draw() {
      const r = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, r.width, r.height);
      ctx.globalCompositeOperation = "lighter";
      for (const p of ambient) {
        p.x += p.vx; p.y += p.vy;
        if (p.y < -4) { p.y = r.height + 4; p.x = Math.random() * r.width; }
        ctx.fillStyle = `rgba(233,178,98,${p.a})`;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill();
      }
      particles = particles.filter((p) => p.life > 0);
      for (const p of particles) {
        p.x += p.vx; p.y += p.vy; p.vx *= 0.97; p.vy = p.vy * 0.97 - 0.01; p.life -= p.decay;
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 4);
        g.addColorStop(0, `rgba(255,226,160,${0.8 * p.life})`);
        g.addColorStop(1, "rgba(233,178,98,0)");
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 4, 0, Math.PI * 2); ctx.fill();
      }
      ctx.globalCompositeOperation = "source-over";
    }
    function loop() {
      if (!ctx) return;
      cancelAnimationFrame(rafId);
      const tick = () => {
        draw();
        if (stageVisible || particles.length) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    }
    function setStage(state, source) {
      stage.dataset.state = state;
      const isOld = state === "old";
      toggle.setAttribute("aria-pressed", String(isOld));
      toggle.textContent = isOld ? text.toNew : text.toOld;
      label.textContent = isOld ? text.nowOld : text.nowNew;
      track("packaging_reveal_interaction", { method: source, state });
    }

    if (toggle) {
      toggle.addEventListener("click", () => {
        const next = stage.dataset.state === "new" ? "old" : "new";
        if (next === "new" && !reduceMotion.matches && ctx) {
          burst();
          setTimeout(() => setStage("new", "hero_toggle"), 260);
        } else {
          setStage(next, "hero_toggle");
        }
      });
    }

    if (ctx && !reduceMotion.matches) {
      sizeCanvas(); seedAmbient(); loop();
      window.addEventListener("resize", () => { sizeCanvas(); seedAmbient(); }, { passive: true });
      if ("IntersectionObserver" in window) {
        new IntersectionObserver(([e]) => {
          stageVisible = e.isIntersecting;
          if (stageVisible) loop();
        }).observe(stage);
      }
    }
  }

  /* ---------- comparison slider ---------- */
  function initCompare(compare) {
    if (compare.dataset.nhwReady) return;
    compare.dataset.nhwReady = "1";
    const range = compare.querySelector(".compare__range");
    if (!range) return;
    let tracked = false;
    range.addEventListener("input", (e) => {
      const pos = Math.max(0, Math.min(100, Number(e.target.value)));
      compare.style.setProperty("--pos", pos + "%");
      range.setAttribute("aria-valuetext",
        pos > 85 ? "Showing mostly the old white label" :
        pos < 15 ? "Showing mostly the new orange label" :
        `Showing ${Math.round(pos)} percent old label, ${Math.round(100 - pos)} percent new label`);
      if (!tracked) { tracked = true; track("packaging_reveal_interaction", { method: "compare_slider", state: "dragged" }); }
    });
  }

  /* ---------- candy "unwrap" tips ---------- */
  function initTreat(btn) {
    if (btn.dataset.nhwReady) return;
    btn.dataset.nhwReady = "1";
    const tip = document.getElementById(btn.getAttribute("aria-controls"));
    btn.addEventListener("click", () => {
      const open = btn.getAttribute("aria-expanded") !== "true";
      btn.setAttribute("aria-expanded", String(open));
      if (tip) tip.classList.toggle("is-open", open);
    });
  }

  /* ---------- purchase type: swap the shown price for Subscribe & Save ---------- */
  function initBuyForm(form) {
    if (form.dataset.nhwReady) return;
    form.dataset.nhwReady = "1";
    form.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
      input.addEventListener("change", () => {
        const sub = input.value !== "" && input.checked;
        form.querySelectorAll("[data-price-onetime]").forEach((el) => {
          el.textContent = sub && el.dataset.priceSubscribe ? el.dataset.priceSubscribe : el.dataset.priceOnetime;
        });
      });
    });
  }

  /* ---------- add to cart: Shopify Cart API, then the native /discount/CODE link ----------
     Captured on window so it runs before the ncl-landing layout's generic /cart/add interceptor
     (a document-level capture listener), which would add the item without applying the code. */
  window.addEventListener("submit", async (e) => {
    const form = e.target;
    if (!form || !form.matches || !form.matches("[data-nhw-buy-form]")) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const status = form.querySelector("[data-nhw-form-status]");
    const cta = form.querySelector('button[type="submit"]');
    if (cta.disabled) return;
    const variantId = Number(form.dataset.variantId);
    const quantity = Number(form.dataset.quantity) || 1;
    const code = form.dataset.discountCode || "";
    const plan = form.querySelector('input[name="selling_plan"]:checked');
    const sellingPlan = plan && plan.value ? Number(plan.value) : 0;
    const line = { id: variantId, quantity };
    if (sellingPlan) line.selling_plan = sellingPlan;

    status.className = "form-status";
    status.textContent = "";
    cta.disabled = true;
    const original = cta.textContent;
    cta.textContent = form.dataset.addingLabel || "Adding your bottles…";
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [line] })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || "Shopify couldn't add this item.");
      const it = item();
      track("add_to_cart", { ecommerce: { currency: "USD", value: (it.price || 0) * quantity, items: [{ ...it, quantity }] } });
      const after = "/cart";
      window.location.assign(code ? `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(after)}` : after);
    } catch (err) {
      status.classList.add("is-error");
      status.textContent = `${err.message} Please try again, or refresh the page.`;
      cta.disabled = false;
      cta.textContent = original;
    }
  }, true);

  /* ---------- sticky purchase bar ---------- */
  let stickyIO = null;
  function initSticky() {
    const sticky = document.querySelector("[data-nhw-sticky]");
    if (!sticky || !("IntersectionObserver" in window)) return;
    if (stickyIO) stickyIO.disconnect();
    const link = sticky.querySelector("a");
    const heroCta = document.querySelector("[data-nhw-hero-cta]");
    const buy = document.getElementById("buy");
    const finale = document.querySelector("[data-nhw-finale]");
    const watched = [heroCta, buy, finale].filter(Boolean);
    const seen = new Map();
    const update = () => {
      const pastHero = heroCta ? seen.get(heroCta) === false : window.scrollY > 200;
      const show = pastHero && !seen.get(buy) && !seen.get(finale);
      sticky.classList.toggle("is-visible", show);
      sticky.setAttribute("aria-hidden", String(!show));
      if (link) link.tabIndex = show ? 0 : -1;
      document.body.style.paddingBottom = show ? sticky.offsetHeight + "px" : "";
    };
    stickyIO = new IntersectionObserver((entries) => {
      entries.forEach((en) => seen.set(en.target, en.isIntersecting));
      update();
    });
    watched.forEach((el) => stickyIO.observe(el));
    if (!heroCta) window.addEventListener("scroll", update, { passive: true });
  }

  /* ---------- wiring ---------- */
  function init(scope) {
    scope.querySelectorAll("[data-nhw-stage]").forEach(initStage);
    scope.querySelectorAll("[data-nhw-compare]").forEach(initCompare);
    scope.querySelectorAll("[data-nhw-treat]").forEach(initTreat);
    scope.querySelectorAll("[data-nhw-buy-form]").forEach(initBuyForm);
    initSticky();
  }

  document.addEventListener("click", (e) => {
    const promo = e.target.closest && e.target.closest(".nhw [data-promo]");
    if (promo) {
      track("select_promotion", { ecommerce: { ...PROMO, creative_slot: promo.dataset.promo, items: [item()] } });
    }
    if (e.target.closest && e.target.closest(".nhw [data-reveal-jump]")) {
      track("packaging_reveal_interaction", { method: "explore_link", state: "jump_to_compare" });
    }
  });

  const start = () => {
    init(document);
    const it = item();
    track("view_item", { ecommerce: { currency: "USD", value: it.price, items: [it] } });
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();

  document.addEventListener("shopify:section:load", (e) => init(e.target));
})();
